import { RouteRecordRaw } from "vue-router";
import authGuard from "@/router/authGuard";
import { BatchRouteNames } from "@/constants/bacs";
import { Permissions } from "@/constants/access";
import { OrganisationRouteNames } from "@/constants/routes";
import settingsGuard from "../settingsGuard";

const schoolRoutes: Array<RouteRecordRaw> = [
    {
        path: "/onboarding",
        name: OrganisationRouteNames.CUSTOMER_ONBOARDING,
        component: () => import("@/views/onboarding/Onboarding.vue"),
        beforeEnter: authGuard,
        meta: {
            requiredPermissions: [Permissions.BASE, Permissions.ORG_ADMIN],
            customerOnboarding: true,
        },
    },
    {
        // Legacy: everything under /school now lives under /organisation.
        // This source must stay /school. Leave it out of any
        // /school -> /organisation find-and-replace, or old links will 404
        // and unknown /organisation URLs will redirect to themselves.
        // Matches /school, /school/ and any depth beneath it, e.g.
        // /school/bacs/credit/batches/42/summary?tab=1#top
        //   -> /organisation/bacs/credit/batches/42/summary?tab=1#top
        path: "/school/:pathMatch(.*)*",
        name: "School",
        redirect: (to) => ({
            path: to.path.replace(/^\/school/, "/organisation"),
            query: to.query,
            hash: to.hash,
        }),
    },
    {
        path: "/organisation",
        name: "Organisation",
        component: () => import("@/views/school/SchoolLayout.vue"),
        beforeEnter: authGuard,
        meta: {
            requiredPermissions: [Permissions.BASE],
        },
        // All child routes render inside SchoolLayout under the organisation namespace.
        children: [
            {
                path: "overview",
                name: OrganisationRouteNames.DASHBOARD,
                component: () => import("@/views/school/overview/Overview.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
            },
            {
                path: "transactions",
                name: "Transactions",
                component: () =>
                    import("@/views/school/transactions/Transactions.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
                children: [
                    {
                        path: "payments",
                        name: "Payments",
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/transactions/categories/Payments.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "direct-debits",
                        name: "DirectDebitsTransactions",
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/transactions/categories/DirectDebits.vue"),
                        meta: {
                            requiredPermissions: [
                                Permissions.BASE,
                                Permissions.DIRECT_DEBIT,
                            ],
                        },
                    },
                    {
                        path: "direct-credit-payroll",
                        name: "DirectCreditPayrollTransactions",
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/transactions/categories/DirectCreditPayroll.vue"),
                        meta: {
                            requiredPermissions: [
                                Permissions.BASE,
                                Permissions.PAYROLL,
                            ],
                        },
                    },
                    {
                        path: "direct-credit-supplier",
                        name: "DirectCreditSupplierTransactions",
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/transactions/categories/DirectCreditSupplier.vue"),
                        meta: {
                            requiredPermissions: [
                                Permissions.BASE,
                                Permissions.SUPPLIER,
                            ],
                        },
                    },
                    {
                        path: "unrecognised-payments",
                        name: OrganisationRouteNames.TRANSACTIONS_UNRECOGNISED_PAYMENTS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/transactions/categories/UnrecognisedPayments.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                ],
            },
            {
                path: "support",
                name: "Support",
                component: () => import("@/views/school/support/Support.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
            },
            {
                path: "settings",
                name: "Settings",
                component: () => import("@/views/school/settings/Settings.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
                children: [
                    {
                        path: "account-information",
                        name: OrganisationRouteNames.ACCOUNT_INFORMATION,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/AccountInformation.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "contact-details",
                        name: OrganisationRouteNames.CONTACT_DETAILS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/ContactDetails.vue"),
                        meta: {
                            requiredPermissions: [Permissions.ORG_ADMIN],
                        },
                    },
                    {
                        path: "posting-settings",
                        name: OrganisationRouteNames.POSTING_SETTINGS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/PostingsSettings.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "financial-and-billing-system-settings",
                        name: OrganisationRouteNames.FINANCIAL_AND_BILLING_SYSTEMS_INTEGRATION,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/FinancialAndBillingSystemsSettings.vue"),
                        meta: {
                            requiredPermissions: [Permissions.RECONCILIATION],
                        },
                    },
                    {
                        path: "api-keys",
                        name: OrganisationRouteNames.API_KEYS,
                        beforeEnter: [
                            authGuard,
                            settingsGuard("show_s3_upload_api_keys"),
                        ],
                        component: () =>
                            import("@/views/school/settings/ApiKeys.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "payment-settings",
                        name: OrganisationRouteNames.PAYMENT_SETTINGS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/PaymentSettings.vue"),
                        meta: {
                            requiredPermissions: [Permissions.ORG_ADMIN],
                        },
                    },
                    {
                        path: "electronic-mandates",
                        name: OrganisationRouteNames.E_MANDATES_SETTINGS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/EMandates.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "user-management",
                        name: OrganisationRouteNames.USER_MANAGEMENT,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/UserManagement.vue"),
                        meta: {
                            requiredPermissions: [Permissions.ORG_ADMIN],
                        },
                    },
                    {
                        path: "bacs-accounts-management",
                        name: OrganisationRouteNames.BACS_ACCOUNTS_MANAGEMENT,
                        beforeEnter: [authGuard, settingsGuard("uses_bacs")],
                        component: () =>
                            import("@/views/school/settings/BacsAccountsManagement.vue"),
                        meta: {
                            requiredPermissions: [Permissions.ORG_ADMIN],
                        },
                    },
                    {
                        path: "identity-provider",
                        name: OrganisationRouteNames.IDENTITY_PROVIDER,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/IdentityProvider.vue"),
                        meta: {
                            requiredPermissions: [Permissions.ORG_ADMIN],
                        },
                    },
                    {
                        path: "notifications",
                        name: OrganisationRouteNames.NOTIFICATION_SETTINGS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/settings/NotificationSettings.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                ],
            },
            {
                path: "payment-request-links",
                name: "PaymentRequestLinks",
                component: () =>
                    import("@/views/school/payment-request-links/PaymentRequestLinks.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
            },
            {
                path: "reconciliation",
                name: OrganisationRouteNames.RECONCILIATION,
                component: () =>
                    import("@/views/school/reconciliation/Reconciliation.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.RECONCILIATION],
                },
                children: [
                    {
                        path: "matches",
                        name: OrganisationRouteNames.RECONCILIATION_MATCHES,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/components/school/reconciliation/matchTransactions.vue"),
                        meta: {
                            requiredPermissions: [Permissions.RECONCILIATION],
                        },
                    },
                    {
                        path: "posting-report",
                        name: OrganisationRouteNames.RECONCILIATION_POSTING_REPORT,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/components/school/reconciliation/postingsReport.vue"),
                        meta: {
                            requiredPermissions: [Permissions.RECONCILIATION],
                        },
                    },
                    {
                        path: "unmatched-and-ignored-transactions",
                        name: OrganisationRouteNames.RECONCILIATION_UNMATCHED_TRANSACTIONS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/components/school/reconciliation/unmatchedTransactions.vue"),
                        meta: {
                            requiredPermissions: [Permissions.RECONCILIATION],
                        },
                    },
                ],
            },
            {
                path: "bacs",
                name: OrganisationRouteNames.BACS,
                beforeEnter: authGuard,
                component: () => import("@/views/school/bacs/Payments.vue"),
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
                children: [
                    {
                        path: ":instructionType/batches",
                        name: OrganisationRouteNames.BACS_BATCHES,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/bacs/batches/BatchList.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                            checkInstructionType: true,
                        },
                    },
                    {
                        path: ":instructionType/batches/:id",
                        name: OrganisationRouteNames.BACS_BATCH,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/bacs/batches/Batch.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                            checkInstructionType: true,
                            // Batch belongs to one organisation. Child step routes
                            // inherit this; :instructionType is reused automatically.
                            orgSwitchFallback: {
                                name: OrganisationRouteNames.BACS_BATCHES,
                            },
                        },
                        children: [
                            {
                                path: "batch-information",
                                name: BatchRouteNames.BATCH_INFORMATION,
                                beforeEnter: authGuard,
                                component: () =>
                                    import("@/components/school/bacs/batches/batch-details/batchInformation.vue"),
                                meta: {
                                    requiredPermissions: [Permissions.BASE],
                                    checkInstructionType: true,
                                },
                            },
                            {
                                path: "select-account",
                                name: BatchRouteNames.SELECT_ACCOUNT,
                                beforeEnter: authGuard,
                                component: () =>
                                    import("@/components/school/bacs/batches/batch-details/selectAccount.vue"),
                                meta: {
                                    requiredPermissions: [Permissions.BASE],
                                    checkInstructionType: true,
                                },
                            },
                            {
                                path: "instructions",
                                name: BatchRouteNames.INSTRUCTIONS,
                                beforeEnter: authGuard,
                                component: () =>
                                    import("@/components/school/bacs/batches/batch-details/instructionsList.vue"),
                                meta: {
                                    requiredPermissions: [Permissions.BASE],
                                    checkInstructionType: true,
                                },
                            },
                            {
                                path: "review-approvals",
                                name: BatchRouteNames.REVIEW_APPROVALS,
                                beforeEnter: authGuard,
                                component: () =>
                                    import("@/components/school/bacs/batches/batch-details/reviewApprovals.vue"),
                                meta: {
                                    requiredPermissions: [Permissions.BASE],
                                    checkInstructionType: true,
                                },
                            },
                            {
                                path: "send-to-bureau",
                                name: BatchRouteNames.SEND_TO_BUREAU,
                                beforeEnter: authGuard,
                                component: () =>
                                    import("@/components/school/bacs/batches/batch-details/sendToBureau.vue"),
                                meta: {
                                    requiredPermissions: [Permissions.BASE],
                                    checkInstructionType: true,
                                },
                            },
                            {
                                path: "summary",
                                name: BatchRouteNames.SUMMARY,
                                beforeEnter: authGuard,
                                component: () =>
                                    import("@/components/school/bacs/batches/batch-details/summary.vue"),
                                meta: {
                                    requiredPermissions: [Permissions.BASE],
                                    checkInstructionType: true,
                                },
                            },
                        ],
                    },
                    {
                        path: "download-file/:type/:fileId/:fileName",
                        name: OrganisationRouteNames.BACS_DOWNLOAD_FILE,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/bacs/DownloadFile.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                            // File belongs to one organisation
                            orgSwitchFallback: {
                                name: OrganisationRouteNames.DASHBOARD,
                            },
                        },
                    },
                    {
                        path: "reports",
                        name: OrganisationRouteNames.BACS_REPORTS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/bacs/reports/Reports.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BACS_REPORTS],
                        },
                    },
                    {
                        path: "reports/:reportID/events",
                        name: OrganisationRouteNames.BACS_REPORT_EVENTS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/bacs/reports/Events.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BACS_REPORTS],
                            // Report belongs to one organisation
                            orgSwitchFallback: {
                                name: OrganisationRouteNames.DASHBOARD,
                            },
                        },
                    },
                ],
            },
            {
                path: "mandates",
                name: OrganisationRouteNames.ALL_MANDATES,
                beforeEnter: authGuard,
                component: () => import("@/views/school/bacs/Mandates.vue"),
                meta: {
                    requiredPermissions: [Permissions.MANDATE],
                },
            },
            {
                path: "users",
                name: OrganisationRouteNames.USERS,
                component: () => import("@/views/school/users/Users.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
            },
            {
                path: "compliance",
                name: OrganisationRouteNames.COMPLIANCE,
                component: () =>
                    import("@/views/school/compliance/Compliance.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
                children: [
                    {
                        path: "company-details",
                        name: OrganisationRouteNames.COMPANY_DETAILS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/compliance/CompanyDetails.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "nature-of-business",
                        name: OrganisationRouteNames.NATURE_OF_BUSINESS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/compliance/NatureOfBusiness.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "financial-system",
                        name: OrganisationRouteNames.FINANCIAL_SYSTEM,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/compliance/FinancialSystem.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "people",
                        name: OrganisationRouteNames.GOVERNANCE,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/compliance/Governance.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "bank-accounts",
                        name: OrganisationRouteNames.BANK_ACCOUNT_INFORMATION,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/compliance/BankAccountInformation.vue"),
                        meta: {
                            requiredPermissions: [Permissions.BASE],
                        },
                    },
                    {
                        path: "card-provider-onboarding",
                        name: OrganisationRouteNames.CARD_PROVIDER_ONBOARDING,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/compliance/CardProviderOnboarding.vue"),
                        meta: {
                            requiredPermissions: [Permissions.ORG_ADMIN],
                        },
                    },
                    {
                        path: "contracts",
                        name: OrganisationRouteNames.CONTRACTS,
                        beforeEnter: authGuard,
                        component: () =>
                            import("@/views/school/compliance/Contracts.vue"),
                        meta: {
                            requiredPermissions: [Permissions.ORG_ADMIN],
                        },
                    },
                ],
            },
            {
                path: "card-payouts",
                name: OrganisationRouteNames.CARD_PAYOUTS,
                component: () =>
                    import("@/views/school/card-payouts/Payouts.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                },
            },
            {
                path: "card-payouts/:payoutId/details",
                name: OrganisationRouteNames.CARD_PAYOUT_DETAILS,
                component: () =>
                    import("@/views/school/card-payouts/PayoutDetails.vue"),
                beforeEnter: authGuard,
                meta: {
                    requiredPermissions: [Permissions.BASE],
                    // Payout belongs to one organisation
                    orgSwitchFallback: {
                        name: OrganisationRouteNames.CARD_PAYOUTS,
                    },
                },
            },
        ],
    },
];

export default schoolRoutes.map((route) => {
    return { ...route };
});
